import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import {
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '../../stores/authStore'
import { useLanguageStore } from '../../stores/languageStore'
import LanguageSwitcher from '../ui/LanguageSwitcher'
import { useAnalytics } from '../../hooks/useAnalytics'
import EnvironmentBadge from '../ui/EnvironmentBadge'
import { getEnvironmentBadges } from '../../utils/environmentBadges'
import Logo from '../ui/Logo'

const Navbar: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const { language } = useLanguageStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const analytics = useAnalytics()
  
  // Get environment badges
  const environmentBadges = getEnvironmentBadges()

  const handleLogout = async () => {
    try {
      await logout()
      analytics.trackAuth.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
      analytics.trackError(new Error('Logout failed'), 'logout_error', 'authentication')
    }
  }

  const navigationItems = [
    { name: t('navigation.home', 'Home'), href: '/', current: false },
    { name: t('navigation.about', 'About'), href: '/about', current: false },
    { name: t('navigation.myAccount', 'My Account'), href: '/dashboard', current: false, auth: true },
    { name: t('navigation.fileLawsuit', 'File Lawsuit'), href: '/file-lawsuit', current: false, auth: true },
    { name: t('navigation.lawsuitFlow', 'Lawsuit Process'), href: '/lawsuit-flow', current: false },
  ]

  const userMenuItems = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: UserIcon },
    { name: t('navigation.profile'), href: '/profile', icon: UserIcon },
    { name: t('navigation.settings'), href: '/settings', icon: Cog6ToothIcon },
  ]

  return (
   <nav className="bg-white shadow-lg border-b">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex justify-between h-16 items-center">
      {/* Logo and brand with environment badges */}
      <div className="flex items-center relative">
        {/* Environment badges - positioned directly on top of logo */}
        {environmentBadges.length > 0 && (
          <div className={`absolute -top-1 ${language === 'ar' || language === 'he' ? '-right-1' : '-left-1'} z-30 flex items-center gap-1 pointer-events-none`}>
            {environmentBadges.map((badge) => (
              <EnvironmentBadge 
                key={badge.type} 
                badge={badge} 
                className="z-10"
              />
            ))}
          </div>
        )}
        <Link to="/" className="flex-shrink-0 flex items-center">
          <Logo 
            width={32} 
            height={32} 
            showText={true} 
            textClassName="text-xl"
          />
        </Link>
      </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => {
              // Hide auth-required items if not authenticated
              if (item.auth && !isAuthenticated) return null
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {item.name}
                </Link>
              )
            })}

            {/* Language switcher */}
            <LanguageSwitcher />

            {/* Authentication buttons */}
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-4">
                {/* Admin link */}
                {user.isAdmin && (
                  <Link
                    to="/admin"
                    className="text-purple-600 hover:text-purple-700 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    {t('navigation.admin')}
                  </Link>
                )}

                {/* User dropdown */}
                <Menu as="div" className="relative">
                  <div>
                    <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <span className="sr-only">Open user menu</span>
                      <div className="flex items-center space-x-2">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="text-gray-700 font-medium">
                          {user.firstName} {user.lastName}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      </div>
                    </Menu.Button>
                  </div>
                  
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className={`absolute ${language === 'ar' || language === 'he' ? 'left-0' : 'right-0'} mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50`}>
                      <div className="py-1">
                        {userMenuItems.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <Link
                                to={item.href}
                                className={`${
                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } group flex items-center px-4 py-2 text-sm`}
                              >
                                <item.icon className="mr-3 h-5 w-5 text-gray-400" />
                                {item.name}
                              </Link>
                            )}
                          </Menu.Item>
                        ))}
                        
                        <div className="border-t border-gray-100"></div>
                        
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex items-center w-full px-4 py-2 text-sm`}
                            >
                              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                              {t('auth.logout')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  {t('auth.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {t('auth.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className={`md:hidden flex items-center ${language === 'ar' || language === 'he' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 p-3 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={isMobileMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu')}
            >
              <span className="sr-only">{isMobileMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu')}</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <Transition
        show={isMobileMenuOpen}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="md:hidden">
          <div className="px-4 pt-4 pb-3 space-y-1 bg-white border-t shadow-lg">
            {navigationItems.map((item) => {
              if (item.auth && !isAuthenticated) return null
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 block px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px] flex items-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            })}

            {isAuthenticated && user ? (
              <>
                <div className="border-t border-gray-200 pt-4 pb-3">
                  <div className={`flex items-center px-4 ${language === 'ar' || language === 'he' ? 'flex-row-reverse' : ''}`}>
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className={`${language === 'ar' || language === 'he' ? 'mr-3' : 'ml-3'}`}>
                      <div className="text-base font-medium text-gray-800">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm font-medium text-gray-500">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 space-y-1">
                    {userMenuItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 flex items-center px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px]"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <item.icon className={`h-5 w-5 text-gray-400 ${language === 'ar' || language === 'he' ? 'ml-3' : 'mr-3'}`} />
                        {item.name}
                      </Link>
                    ))}
                    
                    {user.isAdmin && (
                      <Link
                        to="/admin"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px]"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Cog6ToothIcon className={`h-5 w-5 text-purple-400 ${language === 'ar' || language === 'he' ? 'ml-3' : 'mr-3'}`} />
                        {t('navigation.admin')}
                      </Link>
                    )}
                    
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsMobileMenuOpen(false)
                      }}
                      className="text-gray-700 hover:text-blue-600 hover:bg-red-50 flex items-center w-full px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px]"
                    >
                      <ArrowRightOnRectangleIcon className={`h-5 w-5 text-gray-400 ${language === 'ar' || language === 'he' ? 'ml-3' : 'mr-3'}`} />
                      {t('auth.logout')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="border-t border-gray-200 pt-4 pb-3 space-y-2">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 flex items-center px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('auth.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center px-4 py-4 rounded-lg text-base font-medium transition-all min-h-[44px]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('auth.register')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </Transition>
    </nav>
  )
}

export default Navbar 
